import { useState, useEffect, useMemo } from 'react';
import { Bed, Bath, Maximize2, Star, Eye, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trackEvent } from '@/lib/analytics';
import { formatCurrency, cn } from '@/lib/utils';
import type { UnitConfiguration, PropertyUnit, ExtendedUnitFilters } from '@/lib/units';
import { EmptyFilterState } from './EmptyFilterState';

interface FeaturedConfigurationsViewProps {
  configurations: UnitConfiguration[];
  units: PropertyUnit[];
  onConfigurationClick: (configuration: UnitConfiguration) => void;
  onRegisterClick: () => void;
  activeFilters?: ExtendedUnitFilters;
}

interface FeaturedConfig {
  configuration: UnitConfiguration;
  availableCount: number;
  totalCount: number;
  priceRange: { min: number | null; max: number | null };
  sampleUnit: PropertyUnit | null;
}

export const FeaturedConfigurationsView = ({
  configurations,
  units,
  onConfigurationClick,
  onRegisterClick,
  activeFilters = {},
}: FeaturedConfigurationsViewProps) => {
  const [featuredConfigs, setFeaturedConfigs] = useState<FeaturedConfig[]>([]);

  // Extract category to avoid dependency on entire activeFilters object
  const category = activeFilters?.category;

  // Group configurations by name and calculate stats
  useEffect(() => {
    // Filter configurations by category if active
    const filteredConfigs = category
      ? configurations.filter(config => {
          const categoryMatch = config.bedrooms === (
            category === '1bed' ? 1 :
            category === '2bed' ? 2 :
            3 // townhouse
          );
          return categoryMatch;
        })
      : configurations;

    // Group by name (e.g., "One Bedroom Apartment", "Two Bedroom Apartment", "Townhouse")
    const grouped = filteredConfigs.reduce((acc, config) => {
      if (!acc[config.name]) {
        acc[config.name] = [];
      }
      acc[config.name].push(config);
      return acc;
    }, {} as Record<string, UnitConfiguration[]>);

    // Create featured configs (one per name group)
    const featured: FeaturedConfig[] = [];

    Object.entries(grouped).forEach(([name, configs]) => {
      // Pick the first config as representative
      const representativeConfig = configs[0];

      // Get all units matching ANY of these configurations
      const matchingUnits = units.filter((unit) =>
        configs.some(
          (config) => {
            // Check if it's a townhouse (3 bedrooms)
            const isTownhouse = config.bedrooms === 3;
            
            // Basic matching criteria
            const basicMatch = 
              unit.block === config.block &&
              unit.unit_type === config.unit_type &&
              unit.bedrooms === config.bedrooms &&
              unit.bathrooms === config.bathrooms &&
              (config.location === 'Canal View'
                ? unit.view_type === 'canal'
                : unit.view_type === 'lagoon');
            
            // For townhouses, don't require exact study_room or powder_rooms match
            // (configuration data may be outdated or vary)
            if (isTownhouse) {
              return basicMatch;
            }
            
            // For non-townhouses, require exact match on study_room and powder_rooms
            return basicMatch &&
              unit.study_room === (config.study_room === 1) &&
              unit.powder_rooms === config.powder_room;
          }
        )
      );

      const availableUnits = matchingUnits.filter(
        (unit) => unit.availability === 'available'
      );
      const totalCount = matchingUnits.length;
      const availableCount = availableUnits.length;

      // Calculate price range from available units with prices
      const unitsWithPrice = availableUnits.filter(
        (unit) => unit.show_price && unit.price
      );
      let priceRange = { min: null as number | null, max: null as number | null };
      if (unitsWithPrice.length > 0) {
        const prices = unitsWithPrice.map((unit) => unit.price!);
        priceRange = {
          min: Math.min(...prices),
          max: Math.max(...prices),
        };
      }

      // Get a sample unit with image for the hero card
      const sampleUnit =
        availableUnits.find((unit) => unit.images.length > 0) || availableUnits[0] || null;

      featured.push({
        configuration: representativeConfig,
        availableCount,
        totalCount,
        priceRange,
        sampleUnit,
      });
    });

    setFeaturedConfigs(featured);
  }, [configurations, units, category]);

  const handleConfigClick = (featured: FeaturedConfig) => {
    trackEvent('residences_featured_config_click', {
      configuration_name: featured.configuration.name,
    });
    onConfigurationClick(featured.configuration);
  };

  if (featuredConfigs.length === 0) {
    return (
      <EmptyFilterState 
        browseMode="featured"
        activeFilters={activeFilters.category ? [activeFilters.category] : []}
        onClearFilters={() => window.location.href = '/residences'}
        onViewAllUnits={() => window.location.href = '/residences?tab=all-units'}
      />
    );
  }

  return (
    <div className="py-0" role="region" aria-label="Featured configurations">
      {featuredConfigs.map((featured, index) => {
        const config = featured.configuration;
        const imageUrl =
          featured.sampleUnit?.images[0] || '/img/residence-interior.jpg';
        const hasPriceRange = featured.priceRange.min !== null;

        return (
          <section
            key={config.id}
            className={cn(
              "relative h-screen flex items-center justify-center overflow-hidden group animate-fade-in-scale",
              index > 0 && "animation-delay-200"
            )}
            aria-label={`${config.name} configuration`}
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <img
                src={imageUrl}
                alt={`${config.name} interior view`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            </div>

            {/* Content Overlay */}
            <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl">
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <Badge
                    variant="secondary"
                    className="text-sm bg-brand-teal/20 text-brand-teal border-brand-teal"
                  >
                    {config.bedrooms} Bedroom
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-sm border-white text-white"
                  >
                    {config.location}
                  </Badge>
                  {featured.availableCount > 0 && (
                    <Badge
                      className="text-sm bg-status-available text-white border-status-available"
                    >
                      {featured.availableCount} Available
                    </Badge>
                  )}
                </div>

                <h2 className="font-display text-5xl lg:text-7xl font-bold text-white mb-6 group-hover:text-brand-sky-blue transition-colors duration-500 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] group-hover:drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]">
                  {config.name}
                </h2>

                <div className="flex flex-wrap items-center gap-6 text-white/90 text-xl mb-8">
                  <div className="flex items-center gap-2">
                    <Bed className="w-6 h-6" />
                    <span>
                      {config.bedrooms} bed{config.bedrooms > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bath className="w-6 h-6" />
                    <span>
                      {config.bathrooms} bath{config.bathrooms > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Maximize2 className="w-6 h-6" />
                    <span>{config.total_area_sq_ft.toLocaleString()} sq ft</span>
                  </div>
                  {hasPriceRange && (
                    <div className="inline-flex items-baseline gap-2 px-6 py-3 rounded-2xl bg-black/60 backdrop-blur-md border border-brand-teal/30 shadow-xl">
                      <span className="text-sm font-medium text-white/80 uppercase tracking-wider">Starting From</span>
                      <div className="font-bold text-3xl lg:text-4xl text-brand-teal drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                        {featured.priceRange.min === featured.priceRange.max
                          ? formatCurrency(featured.priceRange.min!)
                          : formatCurrency(featured.priceRange.min!)}
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xl text-white/90 mb-8 max-w-2xl leading-relaxed">
                  {config.bedrooms === 1 &&
                    'Perfect for professionals or couples seeking a low-maintenance luxury lifestyle with stunning views.'}
                  {config.bedrooms === 2 &&
                    'Spacious living for small families or those who work from home, featuring premium finishes and modern design.'}
                  {config.bedrooms === 3 &&
                    'Multi-level townhomes with private outdoor spaces, perfect for families seeking comfort and style.'}
                </p>

                <div className="flex flex-wrap gap-4">
                  <Button
                    size="lg"
                    className="bg-brand-teal hover:bg-brand-navy text-white hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                    onClick={() => handleConfigClick(featured)}
                  >
                    <Eye className="w-5 h-5 mr-2" />
                    View {featured.availableCount} Available Units
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white hover:text-primary hover:scale-105 transition-all duration-300 bg-transparent"
                    onClick={onRegisterClick}
                  >
                    Register Interest
                  </Button>
                </div>
              </div>
            </div>

            {/* Scroll Indicator */}
            {index < featuredConfigs.length - 1 && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
                <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
                  <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-bounce" />
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};
